import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Trash2, Save, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { getAnime, updateAnime, deleteAnime, STATUS_LABELS, STATUS_COLORS } from "../lib/api";

export default function AnimeDetail() {
  const { mal_id } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [userRating, setUserRating] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const data = await getAnime(mal_id);
        setEntry(data);
        setStatus(data.status || "completed");
        setUserRating(data.user_rating ?? "");
        setNotes(data.notes || "");
      } catch {
        toast.error("Not found in your catalogue");
        navigate("/");
      } finally {
        setLoading(false);
      }
    })();
  }, [mal_id, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { status, notes };
      if (userRating !== "" && userRating !== null) payload.user_rating = Number(userRating);
      const updated = await updateAnime(Number(mal_id), payload);
      setEntry(updated);
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Remove this anime from your catalogue?")) return;
    try {
      await deleteAnime(Number(mal_id));
      toast.success("Removed");
      navigate("/");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-6 w-6 animate-spin text-[#FF3B30]" />
      </div>
    );
  }
  if (!entry) return null;

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-8 md:px-10">
      <Link to="/" data-testid="back-link" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-neutral-500 hover:text-white">
        <ArrowLeft className="h-3 w-3" /> Back to catalogue
      </Link>

      <div className="mt-6 grid grid-cols-1 gap-8 md:grid-cols-[300px_1fr] lg:grid-cols-[360px_1fr]">
        {/* Poster + Meta */}
        <div>
          <div className="border border-white/15 bg-neutral-900">
            {entry.image_url && (
              <img src={entry.image_url} alt={entry.title} className="aspect-[2/3] w-full object-cover" />
            )}
          </div>
          <div className="mt-4 divide-y divide-white/10 border border-white/15 font-mono text-[11px] uppercase tracking-widest text-neutral-300">
            <MetaRow label="Type" value={entry.type} />
            <MetaRow label="Episodes" value={entry.episodes || "—"} />
            <MetaRow label="Runtime" value={entry.minutes_per_episode ? `${entry.minutes_per_episode} min/ep` : "—"} />
            <MetaRow label="Total Time" value={entry.total_watch_minutes ? `${Math.round(entry.total_watch_minutes / 60)} hrs` : "—"} />
            <MetaRow label="MAL Score" value={entry.mal_score ? `★ ${entry.mal_score}` : "—"} />
            <MetaRow label="Year" value={entry.year || "—"} />
            <MetaRow label="Airing" value={entry.airing_status || "—"} />
          </div>
          {entry.url && (
            <a
              data-testid="mal-link"
              href={entry.url}
              target="_blank"
              rel="noreferrer"
              className="mt-4 flex items-center justify-center gap-2 border border-white/15 bg-transparent px-4 py-3 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-white hover:text-black"
            >
              <ExternalLink className="h-3 w-3" /> View on MyAnimeList
            </a>
          )}
        </div>

        {/* Content */}
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// #{entry.mal_id}</p>
          <h1 data-testid="anime-title" className="mt-2 font-display text-5xl uppercase leading-[0.95] tracking-tight text-white md:text-7xl">
            {entry.title}
          </h1>
          {entry.title_english && entry.title_english !== entry.title && (
            <p className="mt-2 text-sm text-neutral-500">{entry.title_english}</p>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            {(entry.genres || []).map((g) => (
              <span key={g} className="border border-white/15 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-white">
                {g}
              </span>
            ))}
            {(entry.themes || []).map((t) => (
              <span key={t} className="border border-[#FF3B30]/40 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-[#FF3B30]">
                {t}
              </span>
            ))}
          </div>

          {entry.synopsis && (
            <div className="mt-6 border-l-2 border-[#FF3B30] pl-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">Synopsis</p>
              <p className="mt-2 text-sm leading-relaxed text-neutral-300">{entry.synopsis}</p>
            </div>
          )}

          {/* Edit form */}
          <div className="mt-8 border border-white/15 p-6">
            <h2 className="font-display text-3xl uppercase tracking-tight text-white">Your Notes</h2>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <label className="flex flex-col gap-2">
                <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Status</span>
                <select
                  data-testid="edit-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className={`border bg-black px-3 py-2 font-mono text-[11px] uppercase tracking-widest focus:outline-none focus:border-[#FF3B30] ${STATUS_COLORS[status] || "border-white/15 text-white"}`}
                >
                  {Object.entries(STATUS_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-2">
                <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Your Rating (0-10)</span>
                <div className="flex items-center gap-2 border border-white/15 bg-black px-3">
                  <Star className="h-4 w-4 text-[#FF3B30]" />
                  <input
                    data-testid="edit-rating"
                    type="number"
                    min={0}
                    max={10}
                    step={0.1}
                    value={userRating}
                    onChange={(e) => setUserRating(e.target.value)}
                    className="w-full bg-transparent py-2 text-sm text-white focus:outline-none"
                    placeholder="—"
                  />
                </div>
              </label>
            </div>
            <label className="mt-4 flex flex-col gap-2">
              <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Notes</span>
              <textarea
                data-testid="edit-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                className="w-full resize-none border border-white/15 bg-black px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FF3B30]"
                placeholder="What did you think?"
              />
            </label>
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                data-testid="save-btn"
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 border border-white/15 bg-[#FF3B30] px-5 py-2 font-mono text-[11px] uppercase tracking-widest text-white hover:bg-[#D62828] disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                Save
              </button>
              <button
                data-testid="delete-btn"
                onClick={handleDelete}
                className="flex items-center gap-2 border border-white/15 bg-transparent px-5 py-2 font-mono text-[11px] uppercase tracking-widest text-neutral-400 hover:border-[#FF3B30] hover:text-[#FF3B30]"
              >
                <Trash2 className="h-3 w-3" /> Remove
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-neutral-500">{label}</span>
      <span className="text-white">{value}</span>
    </div>
  );
}
